import { AuthProvider, AccountProvider, RequireAccount } from '../auth';
import { MainLayout } from '../layouts/MainLayout';
import { InternalLayout } from '../layouts/InternalLayout';
import { RequireInternal } from '../components/internal/RequireInternal';

import { HelmetProvider } from 'react-helmet-async';
import { Outlet, Navigate, Route } from 'react-router-dom';
import { Login } from '../layouts/login';
import { ForgotPassword } from '../layouts/ForgotPassword';
import { GatehouseCallback } from '../components/GatehouseCallback';
import { FlowsList } from '../components/flows/FlowsList';
import { FlowEntityContainer } from '../components/flows/FlowEntityContainer';
import { EntityList } from '../components/flowResourceEntity/EntityList';
import { EntityCatalog } from '../components/flowResourceEntity/EntityCatalog';
import { EntityWizard } from '../components/flowResourceEntity/EntityWizard/EntityWizard';
import { ErrorBoundary } from 'react-error-boundary';
import { GlobalSettingsProvider } from './GlobalSettingProvider';
import { ValidationsList } from '../components/validations/ValidationsList';
import { CanvasList } from '../components/canvas/CanvasList';
import { CanvasWizard } from '../components/canvas/CanvasWizard';
import { CanvasView } from '../components/canvas/CanvasView';
import { PageWizard } from '../components/canvas/PageWizard';
import { PageView } from '../components/canvas/PageView';
import { theme } from '../utils/theme';
import { ThemeProvider } from '@emotion/react';
import { TermsOfService } from '../components/TermsOfService';
import { PrivacyPolicy } from '../components/PrivacyPolicy';
import { flowEntityLoader } from '../components/flows/flowEntityLoader';
import RouteErrorBoundary from './RouteErrorBoundary';
import { SyncEntityContainer } from '../components/flows/syncEntity/SyncEntityContainer';
import AdminUsers from '../components/admin/AdminUsers';
import InternalAccounts from '../components/internal/InternalAccounts';
import InternalAccountDetail from '../components/internal/InternalAccountDetail';
import InternalAssumedRoles from '../components/internal/InternalAssumedRoles';
import InternalAuditLog from '../components/internal/InternalAuditLog';
import InternalSupportDashboard from '../components/internal/support/InternalSupportDashboard';
import InternalSupportCases from '../components/internal/support/InternalSupportCases';
import InternalSupportCaseDetail from '../components/internal/support/InternalSupportCaseDetail';
import InternalSettings from '../components/internal/InternalSettings';
import SupportCases from '../components/support/SupportCases';
import SupportCaseDetail from '../components/support/SupportCaseDetail';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
export const muiCache = createCache({
  key: 'mui',
  container: document.querySelector('meta[name="mui-style-insert"]') as Node,
});

export const routes = [
  {
    path: 'terms-of-service',
    element: (
      <CacheProvider value={muiCache}>
        <ThemeProvider theme={theme}>
          <TermsOfService />
        </ThemeProvider>
      </CacheProvider>
    ),
  },
  {
    path: 'privacy-policy',
    element: (
      <CacheProvider value={muiCache}>
        <ThemeProvider theme={theme}>
          <PrivacyPolicy />
        </ThemeProvider>
      </CacheProvider>
    ),
  },
  {
    element: (
      <ErrorBoundary fallback={<div>Something went wrong</div>}>
        <AuthProvider>
          <AccountProvider>
            <HelmetProvider>
              <CacheProvider value={muiCache}>
                <ThemeProvider theme={theme}>
                  <Outlet />
                </ThemeProvider>
              </CacheProvider>
            </HelmetProvider>
          </AccountProvider>
        </AuthProvider>
      </ErrorBoundary>
    ),
    children: [
      {
        path: 'login',
        element: <Login />,
      },
      {
        // Gatehouse OAuth/PKCE callback. Path matches the redirect URI
        // registered with Gatehouse for the aquifer-platform client
        // (see auth/gatehouse-oauth.ts). Resolves to
        // /integrations/auth/callback against the router basename.
        path: 'auth/callback',
        element: <GatehouseCallback />,
      },
      {
        path: 'forgot-password',
        element: <ForgotPassword />,
      },
      {
        path: 'internal',
        element: (
          <RequireInternal>
            <GlobalSettingsProvider>
              <InternalLayout />
            </GlobalSettingsProvider>
          </RequireInternal>
        ),
        children: [
          { index: true, element: <Navigate to="accounts" replace /> },
          { path: 'accounts', element: <InternalAccounts /> },
          {
            path: 'accounts/:accountId',
            element: <InternalAccountDetail />,
          },
          {
            path: 'assumed-roles',
            element: <InternalAssumedRoles />,
          },
          { path: 'audit-log', element: <InternalAuditLog /> },
          { path: 'support', element: <InternalSupportDashboard /> },
          { path: 'support/cases', element: <InternalSupportCases /> },
          {
            path: 'support/cases/:caseId',
            element: <InternalSupportCaseDetail />,
          },
          { path: 'settings', element: <InternalSettings /> },
        ],
      },
      {
        path: '/',
        element: (
          <ErrorBoundary fallback={<div>Something went wrong</div>}>
            <AccountProvider>
              <RequireAccount>
                <GlobalSettingsProvider>
                  <CacheProvider value={muiCache}>
                    <MainLayout />
                  </CacheProvider>
                </GlobalSettingsProvider>
              </RequireAccount>
            </AccountProvider>
          </ErrorBoundary>
        ),
        children: [
          {
            path: ':accountId',
            children: [
              {
                path: 'flows',
                element: <FlowsList />,
                title: 'Flows',
                name: 'flows',
                entityTypes: ['flows'],
                navGroup: 'Data Movement',
              },
              {
                path: 'flows/:entityId',
                element: <FlowEntityContainer />,
                loader: flowEntityLoader,
                title: 'Flow',
                name: 'flow',
                entityTypes: ['flows'],
                navGroup: 'Data Movement',
                errorElement: <RouteErrorBoundary />,
              },
              {
                path: 'flows/:entityId/syncs/:syncId',
                element: <SyncEntityContainer />,
                errorElement: <RouteErrorBoundary />,
                // loader: syncEntityLoader,
                title: 'Sync',
                name: 'sync',
                entityTypes: ['flows', 'syncs'],
              },
              {
                path: ':pluralEntityType',
                element: <EntityList />,
                entityTypes: [
                  'connections',
                  'datastores',
                  'blobstores',
                  'processors',
                ],
                navGroup: 'Data Resources',
              },
              {
                path: ':pluralEntityType/:actionVerb',
                element: <EntityCatalog />,
                entityTypes: [
                  'connections',
                  'datastores',
                  'blobstores',
                  'processors',
                ],
                navGroup: 'Data Resources',
              },

              {
                path: ':pluralEntityType/:actionVerb/:catalogTypeName',
                element: <EntityWizard />,
                entityTypes: [
                  'connections',
                  'datastores',
                  'blobstores',
                  'processors',
                ],
                navGroup: 'Data Resources',
              },
              {
                path: ':pluralEntityType/:actionVerb/:catalogTypeName/:entityName',
                element: <EntityWizard />,
                entityTypes: [
                  'connections',
                  'datastores',
                  'blobstores',
                  'processors',
                ],
                navGroup: 'Data Resources',
              },
              {
                path: 'canvases',
                title: 'Canvases',
                name: 'canvases',
                entityTypes: ['canvases'],
                element: <CanvasList />,
                navGroup: 'Analytics',
              },
              {
                path: 'canvases/create',
                title: 'Create Canvas',
                name: 'canvas-create',
                entityTypes: ['canvases'],
                element: <CanvasWizard />,
                navGroup: 'Analytics',
              },
              {
                path: 'canvases/:canvasSlug',
                title: 'Canvas',
                name: 'canvas',
                entityTypes: ['canvases'],
                element: <CanvasView />,
                navGroup: 'Analytics',
              },
              {
                path: 'canvases/:canvasSlug/pages/create',
                title: 'Create Page',
                name: 'page-create',
                entityTypes: ['canvases'],
                element: <PageWizard />,
                navGroup: 'Analytics',
              },
              {
                path: 'canvases/:canvasSlug/:pageSlug',
                title: 'Canvas Page',
                name: 'canvas-page',
                entityTypes: ['canvases'],
                element: <PageView />,
                navGroup: 'Analytics',
              },
              {
                path: 'validations',
                title: 'Validations',
                name: 'Validations',
                entityTypes: ['validations'],
                element: <ValidationsList />,
                navGroup: 'Data Quality',
              },
              {
                path: 'users',
                element: <AdminUsers />,
                title: 'Users',
                name: 'users',
                entityTypes: ['users'],
                navGroup: 'Account',
              },
              {
                path: 'support',
                element: <SupportCases />,
                title: 'Support',
                name: 'support',
                entityTypes: ['support'],
                navGroup: 'Support',
              },
              {
                path: 'support/:caseId',
                element: <SupportCaseDetail />,
                title: 'Support Case',
                name: 'support-case',
                entityTypes: ['support'],
                navGroup: 'Support',
              },
            ],
          },
        ],
      },
    ],
  },
  // TODO: Add 404 page
];
